import { periodContains, type DashboardPeriod } from "@/lib/dashboard-period";

export interface ProjectLeadHourlyRateProject {
  projectId: string;
  projectName: string;
  projectCode: string | null;
  geekAmount: number;
  loggedHours: number;
  hourlyRate: number | null;
  included: boolean;
}

export interface ProjectLeadHourlyRateRow {
  leadId: string;
  leadName: string;
  geekAmount: number;
  loggedHours: number;
  hourlyRate: number | null;
  includedProjectCount: number;
  excludedProjectCount: number;
  projects: ProjectLeadHourlyRateProject[];
}

interface ProjectInput {
  id: string;
  name: string;
  projectCode: string | null;
  completedAt: string | null;
  productionLeadId: string | null;
  bfActual: number | null;
}

/**
 * Reell timepris per prosjektleder for fullførte prosjekter i valgt periode.
 * GEEK er projects.bf_actual og timer er lifetime på prosjektet; perioden bestemmes av completed_at.
 * Prosjekter uten positive timer vises som datamangel, men påvirker ikke snittet.
 */
export function calcProjectLeadHourlyRates(input: {
  period: DashboardPeriod;
  leads: { id: string; name: string }[];
  projects: ProjectInput[];
  timeEntries: { projectId: string | null; durationSeconds: number | null }[];
}): ProjectLeadHourlyRateRow[] {
  const projects = input.projects.filter(
    (project) =>
      project.productionLeadId &&
      project.completedAt &&
      periodContains(input.period, project.completedAt),
  );

  const hoursByProject = new Map<string, number>();
  for (const entry of input.timeEntries) {
    if (!entry.projectId) continue;
    hoursByProject.set(
      entry.projectId,
      (hoursByProject.get(entry.projectId) ?? 0) + Number(entry.durationSeconds ?? 0) / 3600,
    );
  }

  const rows = input.leads.map((lead): ProjectLeadHourlyRateRow => {
    const leadProjects = projects
      .filter((project) => project.productionLeadId === lead.id)
      .map((project): ProjectLeadHourlyRateProject => {
        const geekAmount = Number(project.bfActual ?? 0);
        const loggedHours = hoursByProject.get(project.id) ?? 0;
        return {
          projectId: project.id,
          projectName: project.name,
          projectCode: project.projectCode,
          geekAmount,
          loggedHours,
          hourlyRate: loggedHours > 0 ? geekAmount / loggedHours : null,
          included: loggedHours > 0,
        };
      })
      .sort((a, b) => {
        if (a.hourlyRate == null && b.hourlyRate == null) return a.projectName.localeCompare(b.projectName, "nb");
        if (a.hourlyRate == null) return 1;
        if (b.hourlyRate == null) return -1;
        return b.hourlyRate - a.hourlyRate;
      });

    const included = leadProjects.filter((project) => project.included);
    const geekAmount = included.reduce((sum, project) => sum + project.geekAmount, 0);
    const loggedHours = included.reduce((sum, project) => sum + project.loggedHours, 0);

    return {
      leadId: lead.id,
      leadName: lead.name,
      geekAmount,
      loggedHours,
      hourlyRate: loggedHours > 0 ? geekAmount / loggedHours : null,
      includedProjectCount: included.length,
      excludedProjectCount: leadProjects.length - included.length,
      projects: leadProjects,
    };
  });

  return rows.sort((a, b) => {
    if (a.hourlyRate == null && b.hourlyRate == null) return a.leadName.localeCompare(b.leadName, "nb");
    if (a.hourlyRate == null) return 1;
    if (b.hourlyRate == null) return -1;
    return b.hourlyRate - a.hourlyRate;
  });
}
